import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import type React from "react";
import {useState} from "react";
import {Controller, type UseFormReturn} from "react-hook-form";
import {
  Button,
  CardLink,
  FormGroup,
  Input,
  Label,
  PopoverBody,
  UncontrolledPopover,
} from "reactstrap";
import {v4 as uuid} from "uuid";

type BaseFieldProps = {
  name: string;
  label?: React.ReactNode;
  hookForm: UseFormReturn<any>;
  noLabel?: boolean;
  disabled?: boolean;
  required?: boolean;
  type?: "text" | "number" | "date" | "select" | "textarea";
  suggestions?: string[];
  onEditSuggestions?: () => void;
  min?: number;
  max?: number;
  style?: React.CSSProperties;
};

type FieldProps = BaseFieldProps & {
  children?: React.ReactNode;
};

export function Field2({
  name,
  label,
  hookForm,
  noLabel,
  disabled,
  required,
  type = "text",
  suggestions,
  onEditSuggestions,
  min,
  max,
  style,
  children,
}: FieldProps) {
  const [key, setKey] = useState("_");
  const id = `field-${name}`;
  const {
    formState: {defaultValues},
  } = hookForm;
  const defaultValue = defaultValues?.[name];

  return (
    <FormGroup>
      <FieldLabel name={name} label={label} required={required} noLabel={noLabel} />
      <Controller
        name={name}
        control={hookForm.control}
        render={({field}) => (
          <>
            {suggestions && (
              <UncontrolledPopover key={key} placement="bottom" target={id} trigger="legacy">
                <PopoverBody
                  style={{
                    minHeight: "260px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <Label>Suggestions :</Label>
                    <ul
                      style={{
                        padding: "0px",
                        margin: "0px",
                        marginBottom: "8px",
                      }}
                    >
                      {suggestions.map((s, index) => (
                        <li
                          key={index}
                          style={{
                            marginTop: "8px",
                            listStyle: "none",
                          }}
                        >
                          <CardLink onClick={() => field.onChange(s)}>
                            <span
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "block",
                              }}
                            >
                              {s}
                            </span>
                          </CardLink>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div style={{display: "flex", justifyContent: "center"}}>
                    <Button
                      color="secondary"
                      id="spectrum-utilisation"
                      type="button"
                      style={{
                        marginTop: "8px",
                        width: "100%",
                      }}
                      onClick={handleEditSuggestions}
                    >
                      <FontAwesomeIcon icon="pen" /> Editer
                    </Button>
                  </div>
                </PopoverBody>
              </UncontrolledPopover>
            )}
            <Input
              {...field}
              onChange={(e) => {
                if (type === "number") {
                  if (!required && e.target.value === "") {
                    field.onChange(null);
                    return;
                  }
                  let value =
                    e.target.value === ""
                      ? min !== undefined
                        ? min
                        : undefined
                      : Number(e.target.value);
                  if (max !== undefined && max < 10) {
                    if (e.target.value.length > 1) {
                      value = Number(e.target.value[e.target.value.length - 1]);
                    }
                  }

                  if (max !== undefined && min !== undefined) {
                    value = Math.min(max, Math.max(min, value ?? 0));
                  } else if (max !== undefined) {
                    value = Math.min(max, value ?? 0);
                  } else if (min !== undefined) {
                    value = Math.max(min, value ?? 0);
                  }
                  e.target.value = value !== undefined ? value.toString() : "";
                  field.onChange(value);
                  return;
                }
                const value = e.target.value;
                field.onChange(value);
              }}
              defaultValue={defaultValue}
              id={id}
              type={type}
              disabled={disabled}
              min={min}
              max={max}
              style={style}
              value={field.value ?? ""}
            >
              {children}
            </Input>
          </>
        )}
      />
    </FormGroup>
  );

  function handleEditSuggestions() {
    if (onEditSuggestions) {
      onEditSuggestions();
      setKey(uuid());
    }
  }
}

function FieldLabel({
  name,
  label,
  required,
  noLabel,
}: {
  name: string;
  label?: React.ReactNode;
  required?: boolean;
  noLabel?: boolean;
}) {
  if (noLabel === true) {
    return null;
  }
  if (typeof label === "string" || label === undefined) {
    const labelString =
      label ?? name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, " $1");
    return (
      <Label>
        {labelString}
        {required && "*"}
      </Label>
    );
  }
  return label;
}
